import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { AppLoader } from '../../Components/common/AppLoader/AppLoader';
import { fetchDigitalCmeById, getCmeIdFromSlug } from '../../api/cmeApi';
import { fetchDoctors } from '../../api/doctorApi';
import { UPLOADS_BASE } from '../../api/config';
import { mapCmeToDetailProps } from '../admin/utils/digitalCmeHelpers';
import { resolveIntroductionVideo } from '../admin/utils/youtubeHelpers';
import aboutusBg from '../../assets/images/aboutus-bg.png';
import '../newsarticals/newsarticals.css';
import '../bizaariostore/BizaarioStorePage.css';
import './cmedetailspage.css';

const buildDoctorsById = (doctors = []) =>
  Object.fromEntries(doctors.map((doc) => [doc.doctor_id, doc]));

const SessionInfo = ({ items }) => {
  const visibleItems = items.filter((item) => item.value && item.value !== '—');
  if (!visibleItems.length) return null;

  return (
    <div className="cme-detail__session-info">
      {visibleItems.map((item, index) => (
        <div key={item.label} className="cme-detail__session-item-wrap">
          {index > 0 ? <div className="cme-detail__session-divider" aria-hidden="true" /> : null}
          <div className="cme-detail__session-item">
            <span className="cme-detail__row-label">{item.label}</span>
            <span className="cme-detail__row-value">{item.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const CmeVideoHero = ({ detail }) => {
  const [playing, setPlaying] = useState(false);
  const canPlay = detail.hasPlay && Boolean(detail.mediaLink);
  const resolved = resolveIntroductionVideo(detail.mediaLink, UPLOADS_BASE);

  useEffect(() => {
    setPlaying(false);
  }, [detail.mediaLink]);

  const handlePlay = () => {
    if (canPlay && resolved) setPlaying(true);
  };

  const renderPlayer = () => {
    if (playing && resolved) {
      if (resolved.kind === 'file') {
        return (
          <video
            controls
            autoPlay
            className="cme-detail__player"
            src={resolved.src}
            preload="metadata"
            playsInline
          >
            <track kind="captions" />
          </video>
        );
      }

      return (
        <iframe
          title={detail.title}
          className="cme-detail__player"
          src={`${resolved.embedUrl}?autoplay=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }

    return (
      <>
        <img src={detail.thumbnail} alt={detail.title} className="cme-detail__thumb" />
        <div className="cme-detail__overlay" />
        {detail.duration ? (
          <span className="cme-detail__duration">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {detail.duration}
          </span>
        ) : null}
        {canPlay ? (
          <button type="button" className="cme-detail__play-btn" onClick={handlePlay} aria-label={`Play ${detail.title}`}>
            <span className="cme-detail__play-triangle" aria-hidden="true" />
          </button>
        ) : (
          <p className="cme-detail__no-video">Video not available</p>
        )}
      </>
    );
  };

  return (
    <div className={`cme-detail__hero${playing ? ' cme-detail__hero--playing' : ''}`}>
      {renderPlayer()}
    </div>
  );
};

export const Cmedetailspage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setDetail(null);

      const cmeId = getCmeIdFromSlug(slug);
      if (!cmeId) {
        setError('CME session not found.');
        setLoading(false);
        return;
      }

      try {
        const [cmeRow, doctorRows] = await Promise.all([
          fetchDigitalCmeById(cmeId),
          fetchDoctors(),
        ]);
        if (cancelled) return;

        const doctorsById = buildDoctorsById(doctorRows);
        const mapped = mapCmeToDetailProps(cmeRow, doctorsById);
        setDetail(mapped);

        if (mapped.slug && mapped.slug !== slug) {
          navigate(`/cme/${mapped.slug}`, { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load CME details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="cme-detail-page">
        <Container className="py-5 text-center">
          <AppLoader message="Loading CME details..." variant="page" />
        </Container>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="cme-detail-page">
        <Container className="py-5 text-center">
          <p className="text-danger">{error || 'CME session not found.'}</p>
          <button type="button" className="cme-detail__back-btn" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </Container>
      </div>
    );
  }

  const hasPresenter = detail.doctorName
    || detail.doctorDesignation
    || detail.doctorSpecialty
    || detail.establishmentName;

  return (
    <div className="cme-detail-page">
      <section
        className="na-hero cme-detail-hero"
        style={{ backgroundImage: `url(${aboutusBg})` }}
      >
        <div className="store-page__hero-overlay">
          <Container>
            <p className="store-page__hero-label">CME Details</p>
            <h1 className="store-page__hero-title">
              Learn from leading doctors and specialists through focused, digestible video content.
            </h1>
          </Container>
        </div>
      </section>

      <Container className="cme-detail__body-wrap">

        <section className="cme-detail__featured">
          <CmeVideoHero detail={detail} />

          <div className="cme-detail__content">
            <div className="cme-detail__card cme-detail__card--title">
              <span className="cme-detail__topic-label">Topic</span>
              <h2 className="cme-detail__topic-title">{detail.title}</h2>
            </div>

            {hasPresenter ? (
              <div className="cme-detail__card">
                <h3 className="cme-detail__section-title">Presenter</h3>
                <div className="cme-detail__presenter">
                  <img src={detail.authorAvatar} alt={detail.doctorName || 'Doctor'} className="cme-detail__presenter-avatar" />
                  <div>
                    {detail.doctorName ? (
                      <p className="cme-detail__presenter-name">{detail.doctorName}</p>
                    ) : null}
                    {detail.doctorDesignation ? (
                      <p className="cme-detail__presenter-meta">{detail.doctorDesignation}</p>
                    ) : null}
                    {detail.doctorSpecialty ? (
                      <p className="cme-detail__presenter-meta">{detail.doctorSpecialty}</p>
                    ) : null}
                    {detail.establishmentName ? (
                      <p className="cme-detail__presenter-hospital">{detail.establishmentName}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="cme-detail__card">
              <h3 className="cme-detail__section-title">Session Info</h3>
              <SessionInfo
                items={[
                  { label: 'Published On', value: detail.publishedOn },
                  { label: 'Last Updated', value: detail.lastUpdatedOn },
                  { label: 'Duration', value: detail.duration },
                  { label: 'Context Tags', value: detail.contextTags },
                ]}
              />
            </div>

            {detail.specialtyLabels.length > 0 ? (
              <div className="cme-detail__card">
                <h3 className="cme-detail__section-title">Medical Specialties</h3>
                <div className="cme-detail__chips">
                  {detail.specialtyLabels.map((label) => (
                    <span key={label} className="cme-detail__chip">{label}</span>
                  ))}
                </div>
              </div>
            ) : null}

            {detail.shortDescription ? (
              <div className="cme-detail__card">
                <h3 className="cme-detail__section-title">Short Description</h3>
                <p className="cme-detail__body">{detail.shortDescription}</p>
              </div>
            ) : null}

            {detail.longDescription ? (
              <div className="cme-detail__card">
                <h3 className="cme-detail__section-title">About This Session</h3>
                <p className="cme-detail__body">{detail.longDescription}</p>
              </div>
            ) : null}

            {detail.referenceImages.length > 0 ? (
              <div className="cme-detail__card">
                <h3 className="cme-detail__section-title">References</h3>
                <div className="cme-detail__references">
                  {detail.referenceImages.map((src, index) => (
                    <img
                      key={`${src}-${index}`}
                      src={src}
                      alt={`Reference ${index + 1}`}
                      className="cme-detail__reference-img"
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </Container>
    </div>
  );
};